import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faClock, faSearch, faFilter, faUser, faVideo, 
    faFileAlt, faCreditCard, faBell, faCog, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface TimelineEvent {
    id: string;
    type: 'session' | 'document' | 'billing' | 'system' | 'user' | 'notification';
    title: string;
    description: string;
    timestamp: string;
    user?: string;
    status: 'success' | 'warning' | 'error' | 'info';
    metadata?: any;
}

const Timeline: React.FC = () => {
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [filteredEvents, setFilteredEvents] = useState<TimelineEvent[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [dateFilter, setDateFilter] = useState<string>('all');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Activity Timeline - Uzima Healthcare";
        loadClinicData();
        loadTimelineEvents();
    }, []);

    useEffect(() => {
        filterEvents();
    }, [events, searchQuery, typeFilter, dateFilter]);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadTimelineEvents = () => {
        // Mock timeline events - in real app, fetch from API
        const mockEvents: TimelineEvent[] = [
            {
                id: 'EVT-001',
                type: 'session',
                title: 'Telehealth Session Completed',
                description: 'Successfully completed telehealth session with Dr. Smith and 3 participants',
                timestamp: '2025-09-05T15:30:00Z',
                user: 'Dr. Smith',
                status: 'success',
                metadata: { sessionId: 'SES-123', duration: '45 minutes', participants: 4 }
            },
            {
                id: 'EVT-002',
                type: 'billing',
                title: 'Payment Processed',
                description: 'Monthly subscription payment of $299.00 processed successfully',
                timestamp: '2025-09-05T10:15:00Z',
                status: 'success',
                metadata: { amount: '$299.00', method: 'Credit Card', invoiceId: 'INV-456' }
            },
            {
                id: 'EVT-003',
                type: 'user',
                title: 'New User Added',
                description: 'New healthcare professional Dr. Johnson added to the system',
                timestamp: '2025-09-05T09:45:00Z',
                user: 'Admin',
                status: 'info',
                metadata: { userId: 'USR-789', role: 'Healthcare Professional' }
            },
            {
                id: 'EVT-004',
                type: 'document',
                title: 'Service Request Created',
                description: 'New service request created for patient John Doe',
                timestamp: '2025-09-04T16:20:00Z',
                user: 'Dr. Smith',
                status: 'info',
                metadata: { requestId: 'REQ-101', patientId: 'PAT-202' }
            },
            {
                id: 'EVT-005',
                type: 'system',
                title: 'System Backup Completed',
                description: 'Daily system backup completed successfully',
                timestamp: '2025-09-04T03:00:00Z',
                status: 'success',
                metadata: { backupSize: '2.5 GB', duration: '15 minutes' }
            },
            {
                id: 'EVT-006',
                type: 'notification',
                title: 'Maintenance Notice Sent',
                description: 'Scheduled maintenance notice sent to all users',
                timestamp: '2025-09-03T14:30:00Z',
                status: 'warning',
                metadata: { recipients: 150, type: 'maintenance' }
            },
            {
                id: 'EVT-007',
                type: 'session',
                title: 'Session Recording Saved',
                description: 'Telehealth session recording saved to cloud storage',
                timestamp: '2025-09-03T11:15:00Z',
                user: 'Dr. Brown',
                status: 'success',
                metadata: { sessionId: 'SES-124', fileSize: '1.2 GB' }
            },
            {
                id: 'EVT-008',
                type: 'billing',
                title: 'Invoice Generated',
                description: 'Monthly invoice generated for September 2025',
                timestamp: '2025-09-01T08:00:00Z',
                status: 'info',
                metadata: { invoiceId: 'INV-457', amount: '$299.00' }
            }
        ];
        setEvents(mockEvents);
    };

    const filterEvents = () => {
        let filtered = events;

        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(event => 
                event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (event.user && event.user.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        // Filter by type
        if (typeFilter !== 'all') {
            filtered = filtered.filter(event => event.type === typeFilter);
        }

        // Filter by date
        if (dateFilter !== 'all') {
            const now = new Date();
            const filterDate = new Date();
            
            switch (dateFilter) {
                case 'today':
                    filterDate.setHours(0, 0, 0, 0);
                    break;
                case 'week':
                    filterDate.setDate(now.getDate() - 7);
                    break;
                case 'month':
                    filterDate.setMonth(now.getMonth() - 1);
                    break;
            }
            
            filtered = filtered.filter(event => new Date(event.timestamp) >= filterDate);
        }

        setFilteredEvents(filtered);
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'session': return faVideo;
            case 'document': return faFileAlt;
            case 'billing': return faCreditCard;
            case 'system': return faCog;
            case 'user': return faUser;
            case 'notification': return faBell;
            default: return faClock;
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'session': return 'primary';
            case 'document': return 'info';
            case 'billing': return 'success';
            case 'system': return 'secondary';
            case 'user': return 'warning';
            case 'notification': return 'danger';
            default: return 'secondary';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success': return 'success';
            case 'warning': return 'warning';
            case 'error': return 'danger';
            case 'info': return 'info';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRelativeTime = (dateString: string) => {
        const now = new Date();
        const eventDate = new Date(dateString);
        const diffInHours = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        if (diffInHours < 48) return 'Yesterday';
        return `${Math.floor(diffInHours / 24)} days ago`;
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                                    Activity Timeline
                                </h2>
                                <p className="text-muted mb-0">
                                    Track all activities and events for {clinicName}
                                </p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search timeline events..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="session">Sessions</option>
                                    <option value="document">Documents</option>
                                    <option value="billing">Billing</option>
                                    <option value="system">System</option>
                                    <option value="user">Users</option>
                                    <option value="notification">Notifications</option>
                                </select>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                >
                                    <option value="all">All Time</option>
                                    <option value="today">Today</option>
                                    <option value="week">This Week</option>
                                    <option value="month">This Month</option>
                                </select>
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="row">
                            <div className="col-12">
                                <div className="timeline">
                                    {filteredEvents.map((event, index) => (
                                        <div key={event.id} className="timeline-item">
                                            <div className="timeline-marker">
                                                <FontAwesomeIcon 
                                                    icon={getTypeIcon(event.type)} 
                                                    className={`text-${getTypeColor(event.type)}`}
                                                />
                                            </div>
                                            <div className="timeline-content">
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body">
                                                        <div className="d-flex align-items-start justify-content-between">
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <h6 className="mb-0">{event.title}</h6>
                                                                    <span className={`badge bg-${getStatusColor(event.status)}`}>
                                                                        {event.status.toUpperCase()}
                                                                    </span>
                                                                    <span className={`badge bg-${getTypeColor(event.type)} bg-opacity-20 text-${getTypeColor(event.type)}`}>
                                                                        {event.type.toUpperCase()}
                                                                    </span>
                                                                </div>
                                                                <p className="text-muted mb-2">{event.description}</p>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <small className="text-muted">
                                                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                        {formatDate(event.timestamp)}
                                                                    </small>
                                                                    <small className="text-muted">
                                                                        {getRelativeTime(event.timestamp)}
                                                                    </small>
                                                                    {event.user && (
                                                                        <small className="text-muted">
                                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                                            {event.user}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* No Results */}
                        {filteredEvents.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faClock} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No events found</h5>
                                <p className="text-muted">
                                    Try adjusting your search terms or filters
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
            
            <style>{`
                .timeline {
                    position: relative;
                    padding-left: 30px;
                }
                
                .timeline::before {
                    content: '';
                    position: absolute;
                    left: 15px;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: #e9ecef;
                }
                
                .timeline-item {
                    position: relative;
                    margin-bottom: 30px;
                }
                
                .timeline-marker {
                    position: absolute;
                    left: -22px;
                    top: 20px;
                    width: 40px;
                    height: 40px;
                    background: white;
                    border: 3px solid #e9ecef;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1;
                }
                
                .timeline-content {
                    margin-left: 20px;
                }
            `}</style>
        </>
    );
};

export default Timeline;
